import React, { useContext, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Link } from 'react-router-dom';
import Drawer from '~/components/Drawer';
import PersonDetails from './PersonDetails';
import { sectionStyles } from './styles';
import AdditionalPersonInfo from './ClientInfoDrawer/AdditionalPersonalInfo';
import { putPatient, putCampaignApplicationPatient } from '~/services/campaign';
import { normalizePhone, FormCheckbox } from '~/common/form/FormInput';
import PasswordManagement from './PasswordManagement';
import { CampaignContext } from '~/context/CampaignProvider';
import { toast } from 'react-hot-toast';

interface Props {
    isOpen: boolean;
    campaign: any;
    setIsOpen: any;
    person: any;
    patient: any;
    application?: any;
    states: any;
    preferredLanguages: string[];
    maritalStatuses: string[];
    ethnicities: string[];
    onDataSaved?: () => void;
}

type DefaultEditPatient = {
    title: string;
    suffix: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    nicknames: string;
    update_photo: boolean;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    gender: string;
    birth_date: string;
    patient_is_deceased: boolean;
    death_date: string;
    marital_status: string;
    ethnicity: string;
    preferred_language: string;
    email: string;
    phone: string;
    use_temporary_address: boolean;
    temporary_address_1: string;
    temporary_address_2: string;
    temporary_city: string;
    temporary_state: string;
    temporary_zip_code: string;
    temporary_country: string;
    temporary_from_date: string;
    temporary_to_date: string;
};

const defaultEditPatient: DefaultEditPatient = {
    title: '',
    suffix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    nicknames: '',
    update_photo: false,
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    gender: '',
    birth_date: '',
    patient_is_deceased: false,
    death_date: '',
    marital_status: '',
    ethnicity: '',
    preferred_language: '',
    email: '',
    phone: '',
    use_temporary_address: false,
    temporary_address_1: '',
    temporary_address_2: '',
    temporary_city: '',
    temporary_state: '',
    temporary_zip_code: '',
    temporary_country: '',
    temporary_from_date: '',
    temporary_to_date: '',
};

const ClientInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    person,
    patient,
    application,
    states,
    preferredLanguages,
    maritalStatuses,
    ethnicities,
    campaign,
    onDataSaved,
}) => {
    const [{ campaignData }, campaignDispatch] = useContext(CampaignContext);
    
    // Ensure phone_set has at least one entry for the form
    const personWithPhone = React.useMemo(() => ({
        ...person,
        phone_set: person?.phone_set?.length > 0 
            ? person.phone_set 
            : [{ phone: '', type: 'Cell' }]
    }), [person]);
    
    const methods = useForm({
        defaultValues: {
            person: personWithPhone,
            patient,
            direct_vendor_pay_only: campaign?.direct_vendor_pay_only ?? false,
        },
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Reset form with latest person/patient data when drawer opens
    React.useEffect(() => {
        if (isOpen) {
            // Helper function to convert null to empty string for text inputs
            const sanitizeValue = (value: any) => value ?? '';
            
            // Only include fields that are actually used in this form
            const formattedPerson = {
                // Contact information (used in PersonDetails)
                title: sanitizeValue(personWithPhone?.title),
                first_name: sanitizeValue(personWithPhone?.first_name),
                middle_name: sanitizeValue(personWithPhone?.middle_name),
                last_name: sanitizeValue(personWithPhone?.last_name),
                suffix: sanitizeValue(personWithPhone?.suffix),
                nicknames: sanitizeValue(personWithPhone?.nicknames),
                search_names: sanitizeValue(personWithPhone?.search_names),
                email: sanitizeValue(personWithPhone?.email),
                phone: sanitizeValue(personWithPhone?.phone),
                // Normalize stored numbers (e.g. legacy 727-418-4219) to the
                // (123) 456-7890 format so saving doesn't trip the pattern rule.
                phone_set: personWithPhone?.phone_set?.length > 0
                    ? personWithPhone.phone_set.map((p: any) => ({
                          ...p,
                          phone: normalizePhone(p?.phone || ''),
                      }))
                    : [{ phone: '', type: 'Cell' }],
                address_1: sanitizeValue(personWithPhone?.address_1),
                address_2: sanitizeValue(personWithPhone?.address_2),
                city: sanitizeValue(personWithPhone?.city),
                state: personWithPhone?.state,
                zip_code: sanitizeValue(personWithPhone?.zip_code),
                // Personal information (used in AdditionalPersonInfo)
                birth_date: sanitizeValue(personWithPhone?.birth_date),
                age: sanitizeValue(personWithPhone?.age),
                gender: personWithPhone?.gender 
                    ? { label: personWithPhone.gender, value: personWithPhone.gender }
                    : { label: '', value: '' },
                pronouns: sanitizeValue(personWithPhone?.pronouns),
                marital_status: personWithPhone?.marital_status
                    ? { label: personWithPhone.marital_status, value: personWithPhone.marital_status }
                    : { label: '', value: '' },
                preferred_language: personWithPhone?.preferred_language
                    ? { label: personWithPhone.preferred_language, value: personWithPhone.preferred_language }
                    : { label: '', value: '' },
                patient_is_deceased: personWithPhone?.patient_is_deceased || false,
                death_date: sanitizeValue(personWithPhone?.death_date),
            };
            
            // Only include fields used in AdditionalPersonInfo
            const formattedPatient = {
                ethnicity: patient?.ethnicity
                    ? (typeof patient.ethnicity === 'object' && patient.ethnicity !== null
                        ? patient.ethnicity
                        : { label: patient.ethnicity, value: patient.ethnicity })
                    : { label: '', value: '' },
            };
            
            methods.reset({
                person: formattedPerson,
                patient: formattedPatient,
                direct_vendor_pay_only:
                    campaign?.direct_vendor_pay_only ?? false,
            });
        }
    }, [isOpen, personWithPhone, patient, campaign?.direct_vendor_pay_only, methods]);
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const personData = data?.person;
            const patientData = data?.patient;

            // Convert empty date strings to null to avoid Django validation errors
            const payload = {
                ...defaultEditPatient,
                ...campaign,
                ...personData,
                ...data,
                // These need to come AFTER ...data to ensure they don't get overwritten
                gender: personData?.gender?.value ?? personData?.gender,
                marital_status:
                    personData?.marital_status?.value ??
                    personData?.marital_status,
                preferred_language:
                    personData?.preferred_language?.value ??
                    personData?.preferred_language,
                ethnicity:
                    patientData?.ethnicity?.value ?? patientData?.ethnicity,
                state: personData?.state,
                admin: true, // This is required to return the correct payload since it's talking to the same endpoint as the client app
            };

            // Convert empty date strings to null
            [
                'birth_date',
                'death_date',
                'temporary_from_date',
                'temporary_to_date',
            ].forEach((field) => {
                if (payload[field] === '') {
                    payload[field] = null;
                }
            });

            let updatedCampaignData;
            let accountEmailSent = false;

            // If application exists, use the application endpoint
            // Otherwise, use the legacy patient endpoint
            if (application?.id) {
                // For application endpoint, include all client info fields
                const applicationPayload = {
                    // Contact information
                    title: payload.title,
                    first_name: payload.first_name,
                    middle_name: payload.middle_name,
                    last_name: payload.last_name,
                    suffix: payload.suffix,
                    nicknames: payload.nicknames,
                    search_names: personData?.search_names,
                    address_1: payload.address_1,
                    address_2: payload.address_2,
                    city: payload.city,
                    state: payload.state,
                    zip_code: payload.zip_code,
                    email: payload.email,
                    // Send phone_set array (filter out empty phone numbers)
                    phone_set: (personData?.phone_set || []).filter(
                        (p: any) => p?.phone?.trim()
                    ),
                    // Personal information
                    birth_date: payload.birth_date,
                    gender: payload.gender,
                    marital_status: payload.marital_status,
                    preferred_language: payload.preferred_language,
                    pronouns: payload.pronouns,
                    // Deceased information
                    patient_is_deceased: payload.patient_is_deceased,
                    death_date: payload.death_date,
                    // Patient-specific fields
                    ethnicity: payload.ethnicity,
                    // Campaign-level flag
                    direct_vendor_pay_only: payload.direct_vendor_pay_only,
                };
                
                const apiResponse = await putCampaignApplicationPatient(
                    application.id,
                    applicationPayload
                );

                accountEmailSent = !!apiResponse?.account_email_sent;

                // Transform API response to match context structure
                const {
                    campaign: campaignData,
                    campaign_id,
                    patient: patientData,
                    csc,
                    campaign_request,
                    notes,
                    coordinator,
                    approval,
                    financial_coordinator,
                    ...applicationFields
                } = apiResponse;

                updatedCampaignData = {
                    campaign: campaignData,
                    patient: patientData,
                    csc,
                    campaign_request,
                    notes,
                    campaignPhotoInfo: null,
                    coordinator,
                    financial_coordinator,
                    count: 0,
                    application: {
                        ...applicationFields,
                        campaign: campaign_id,
                    },
                    approval,
                };
            } else {
                // Use legacy endpoint
                // Filter out empty phone numbers for the legacy endpoint too
                const legacyPayload = {
                    ...payload,
                    phone_set: (personData?.phone_set || []).filter(
                        (p: any) => p?.phone?.trim()
                    ),
                };
                updatedCampaignData = await putPatient(legacyPayload);
                accountEmailSent = !!updatedCampaignData?.account_email_sent;
            }

            campaignDispatch({
                type: 'set_campaign',
                payload: updatedCampaignData,
            });

            toast.success('Client info updated');
            if (accountEmailSent) {
                toast.success('Account setup email sent');
            }

            setIsOpen(false);

            // Refresh data after successful save
            if (onDataSaved) {
                onDataSaved();
            }
        } catch (error: any) {
            console.error('Error submitting form:', error);
            
            // Parse error message from response
            let errorMessage = 'Failed to update client information';
            
            if (error?.detail) {
                errorMessage = error.detail;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const drawerTitle = (
        <span className='inline-flex items-center gap-2'>
            Client Info
            {person?.user && (
                <Link
                    to={`/users/${person.user}`}
                    className='text-primary hover:opacity-70'
                    title='View User Profile'
                >
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                        />
                    </svg>
                </Link>
            )}
        </span>
    );

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title={drawerTitle}>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <div className='mb-6'>
                        <FormCheckbox
                            name='direct_vendor_pay_only'
                            label='This campaign is Direct Vendor Pay Only'
                        />
                        <p className='mt-1 pl-6 text-[13px]/[16px] text-gray-500'>
                            A GoFundMe campaign exists for this client
                        </p>
                    </div>

                    <h2 className={sectionStyles}>CONTACT</h2>

                    <PersonDetails
                        multiplePhoneNumbers={true}
                        states={states}
                    />

                    <h2 className={sectionStyles}>PERSONAL INFO</h2>

                    <AdditionalPersonInfo
                        person={person}
                        patient={patient}
                        maritalStatuses={maritalStatuses}
                        preferredLanguages={preferredLanguages}
                        ethnicities={ethnicities}
                    />

                    {person?.user && person?.email && (
                        <PasswordManagement
                            userId={person.user}
                            userName={`${person.first_name} ${person.last_name}`}
                            onPasswordChanged={onDataSaved}
                        />
                    )}

                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => setIsOpen(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span className='flex items-center justify-center'>
                                    <svg
                                        className='mr-3 h-5 w-5 animate-spin'
                                        viewBox='0 0 24 24'
                                    >
                                        <circle
                                            className='opacity-25'
                                            cx='12'
                                            cy='12'
                                            r='10'
                                            stroke='currentColor'
                                            strokeWidth='4'
                                        />
                                        <path
                                            className='opacity-75'
                                            fill='currentColor'
                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                        />
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default ClientInfoDrawer;
